import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { PLATFORM } from "../env";
import type { TenantDriverMembership } from "../types";

interface Props {
  onOpenDriver: (id: number) => void;
  onOpenTenant: (id: number) => void;
}

interface MembershipsPage { items: TenantDriverMembership[]; }

const LIMIT = 50;

export default function Memberships({ onOpenDriver, onOpenTenant }: Props) {
  const authApi = useAuthApi();
  const [memberships, setMemberships] = useState<TenantDriverMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [driverIdFilter, setDriverIdFilter] = useState("");
  const [tenantIdFilter, setTenantIdFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
    if (driverIdFilter) params.set("driver_id", driverIdFilter);
    if (tenantIdFilter) params.set("tenant_id", tenantIdFilter);

    authApi<MembershipsPage>(`${PLATFORM}/memberships?${params}`)
      .then((data) => {
        if (!cancelled) setMemberships(data?.items ?? []);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load memberships");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [authApi, offset, driverIdFilter, tenantIdFilter]);

  const handleFilter = () => {
    setOffset(0);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "flex-end" }}>
        <TextField
          label="Driver ID"
          size="small"
          value={driverIdFilter}
          onChange={(e) => setDriverIdFilter(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleFilter()}
        />
        <TextField
          label="Tenant ID"
          size="small"
          value={tenantIdFilter}
          onChange={(e) => setTenantIdFilter(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleFilter()}
        />
        <Button variant="outlined" onClick={handleFilter}>Filter</Button>
      </Box>

      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Driver ID</TableCell>
                <TableCell>Tenant ID</TableCell>
                <TableCell>Join Mode</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {memberships.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{m.id}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => onOpenDriver(m.driver_id)}>
                      {m.driver_id}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => onOpenTenant(m.tenant_id)}>
                      {m.tenant_id}
                    </Button>
                  </TableCell>
                  <TableCell>{m.join_mode}</TableCell>
                  <TableCell>
                    <Chip label={m.status} color={m.status === "active" ? "success" : "warning"} size="small" />
                  </TableCell>
                  <TableCell>{new Date(m.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
              {memberships.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">No membership records found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button
              size="small"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
            >
              Previous
            </Button>
            <Button
              size="small"
              disabled={memberships.length < LIMIT}
              onClick={() => setOffset((o) => o + LIMIT)}
            >
              Next
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
